// Shared-link → native app handoff.
//
// When a shared link like /events?event=123 is opened on a phone, we want the
// native app to take priority. Universal Links / App Links handle this silently
// when the link is *tapped* from another app (Messages, Mail, etc.), but they
// deliberately do NOT fire for pasted URLs, same-domain navigation, or in-app
// browsers (Instagram, Twitter…). Those arrivals land in the web app instead, so
// we attempt the native scheme here. When the app is NOT installed we send the
// visitor to the relevant store — Play Store on Android, App Store on iOS — so a
// shared link always resolves to either an app-open or an install.

const SCHEME = 'mycarcollection';
const ANDROID_PACKAGE = 'com.yng.mycarcollection';
const PLAY_STORE_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
// Apple ID for "MyCarCollection X". No /us/ segment so visitors auto-route to
// their local storefront. Keep in sync with APP_STORE_URL in pages/Landing.jsx.
const APP_STORE_URL = 'https://apps.apple.com/app/mycarcollection-x/id6767995166';

const detectPlatform = () => {
  const ua = navigator.userAgent || '';
  return {
    isIOS: /iPad|iPhone|iPod/.test(ua) && !window.MSStream,
    isAndroid: /Android/.test(ua),
  };
};

// Map a web URL (pathname + search) to the equivalent deep-link path. Returns
// null when the URL isn't a recognised shareable target.
const toDeepLinkTarget = (pathname, search) => {
  const params = new URLSearchParams(search);
  const eventId = params.get('event');
  if (pathname.startsWith('/events') && eventId) {
    return { path: `events?event=${encodeURIComponent(eventId)}`, key: `event_${eventId}` };
  }
  // Routes and landmarks both live on the web /routes page.
  if (pathname.startsWith('/routes')) {
    const routeId = params.get('route');
    if (routeId) {
      return { path: `routes?route=${encodeURIComponent(routeId)}`, key: `route_${routeId}` };
    }
    const landmarkId = params.get('landmark');
    if (landmarkId) {
      return { path: `routes?landmark=${encodeURIComponent(landmarkId)}`, key: `landmark_${landmarkId}` };
    }
  }
  return null;
};

// Send an uninstalled iOS visitor to the App Store. iOS has no
// browser_fallback_url for custom schemes, so we detect "app didn't open" by
// watching whether the tab gets backgrounded: if the app launches, the page is
// hidden and we cancel; otherwise the timer fires and we redirect to the store.
const scheduleIosStoreFallback = () => {
  let appLaunched = false;
  const onVisibility = () => { if (document.hidden) appLaunched = true; };
  const onHide = () => { appLaunched = true; };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onHide, { once: true });
  setTimeout(() => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', onHide);
    if (!appLaunched && !document.hidden) {
      window.location.href = APP_STORE_URL;
    }
  }, 1500);
};

// Fire the native open for the given path, falling back to the platform's app
// store when the app isn't installed.
const redirectToApp = (path) => {
  const { isAndroid, isIOS } = detectPlatform();

  if (isAndroid) {
    // intent:// lets Chrome cleanly choose app-or-store: if the app isn't
    // installed it follows browser_fallback_url (the Play Store) instead of
    // erroring.
    const fallback = encodeURIComponent(PLAY_STORE_URL);
    window.location.href = `intent://${path}#Intent;scheme=${SCHEME};package=${ANDROID_PACKAGE};S.browser_fallback_url=${fallback};end`;
    return;
  }

  if (isIOS) {
    scheduleIosStoreFallback();
    window.location.href = `${SCHEME}://${path}`;
  }
};

// True when the current page is a shareable target opened on a phone.
export const isMobileSharedLink = (pathname, search) =>
  Boolean(toDeepLinkTarget(pathname, search)) &&
  (detectPlatform().isIOS || detectPlatform().isAndroid);

// Auto-attempt the native open once per shared link (guarded so navigating back
// to the browser tab doesn't re-trigger the handoff). Runs before any auth gate
// so a shared link prioritises the app even for logged-out visitors; if the app
// isn't installed the visitor is taken to the relevant store to install it.
// Returns true if this page is a mobile shared link (caller may show a banner).
export const attemptOpenInApp = (pathname, search) => {
  const target = toDeepLinkTarget(pathname, search);
  if (!target) return false;
  const { isIOS, isAndroid } = detectPlatform();
  if (!isIOS && !isAndroid) return false;

  const storageKey = `mcc_dl_${target.key}`;
  if (!sessionStorage.getItem(storageKey)) {
    sessionStorage.setItem(storageKey, '1');
    redirectToApp(target.path);
  }
  return true;
};

// Manual "Open in app" button handler — always fires, no guard.
export const openInApp = (pathname, search) => {
  const target = toDeepLinkTarget(pathname, search);
  if (target) redirectToApp(target.path);
};
